import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CustomResponsesInputs, missingCustom, responsesToMap } from './CustomFields';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { API } from '../../utils/apiConfig';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ArrowLeft, Search, Download, UserPlus, ScrollText, Loader2, IndianRupee, Users, Clock, CheckCircle, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Layers, ClipboardCopy, Send, Ghost, CalendarX2 } from 'lucide-react';
import { PAYMENT_MODES, TSHIRT_SIZES, GENDERS, FOOD_PREFS, SELF_RATINGS, statusLabel } from './eventUtils';

const emptyPerson = () => ({ name: '', mobile: '', email: '', gender: '', date_of_birth: '', city: '', emergency_contact: '', dupr_id: '', dupr_rating: '', tshirt_size: '', food_preference: '', self_rating: '' });

function PersonForm({ label, person, onChange, onAutoLookup, testPrefix }) {
  const timerRef = useRef(null);
  const lastLookedUpRef = useRef('');

  const handleMobileChange = (raw) => {
    const cleaned = (raw || '').replace(/\D/g, '').slice(0, 10);
    onChange({ ...person, mobile: cleaned });
    if (!onAutoLookup) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (cleaned.length !== 10) { lastLookedUpRef.current = ''; return; }
    if (lastLookedUpRef.current === cleaned) return;
    timerRef.current = setTimeout(() => {
      lastLookedUpRef.current = cleaned;
      onAutoLookup(cleaned);
    }, 400);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">{label} Mobile</Label>
        <Input value={person.mobile} onChange={(e) => handleMobileChange(e.target.value)} data-testid={`${testPrefix}-mobile`} placeholder="10-digit mobile (auto-fills from profile)" inputMode="numeric" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1"><Label className="text-xs">Name</Label><Input value={person.name} onChange={(e) => onChange({ ...person, name: e.target.value })} data-testid={`${testPrefix}-name`} /></div>
        <div className="space-y-1"><Label className="text-xs">Email</Label><Input value={person.email} onChange={(e) => onChange({ ...person, email: e.target.value })} data-testid={`${testPrefix}-email`} /></div>
        <div className="space-y-1">
          <Label className="text-xs">Gender</Label>
          <Select value={person.gender || ''} onValueChange={(v) => onChange({ ...person, gender: v })}>
            <SelectTrigger data-testid={`${testPrefix}-gender`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>{GENDERS.map((g) => <SelectItem key={g} value={g}>{statusLabel(g)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1"><Label className="text-xs">Date of Birth</Label><Input type="date" value={person.date_of_birth || ''} onChange={(e) => onChange({ ...person, date_of_birth: e.target.value })} data-testid={`${testPrefix}-dob`} /></div>
        <div className="space-y-1"><Label className="text-xs">City</Label><Input value={person.city || ''} onChange={(e) => onChange({ ...person, city: e.target.value })} data-testid={`${testPrefix}-city`} /></div>
        <div className="space-y-1"><Label className="text-xs">Emergency Contact</Label><Input value={person.emergency_contact || ''} onChange={(e) => onChange({ ...person, emergency_contact: e.target.value })} data-testid={`${testPrefix}-emergency`} /></div>
        <div className="space-y-1"><Label className="text-xs">DUPR ID</Label><Input value={person.dupr_id || ''} onChange={(e) => onChange({ ...person, dupr_id: e.target.value })} data-testid={`${testPrefix}-dupr-id`} /></div>
        <div className="space-y-1"><Label className="text-xs">DUPR Rating</Label><Input type="number" step="0.1" value={person.dupr_rating || ''} onChange={(e) => onChange({ ...person, dupr_rating: e.target.value })} data-testid={`${testPrefix}-dupr-rating`} /></div>
        <div className="space-y-1">
          <Label className="text-xs">T-shirt</Label>
          <Select value={person.tshirt_size || ''} onValueChange={(v) => onChange({ ...person, tshirt_size: v })}>
            <SelectTrigger data-testid={`${testPrefix}-tshirt`}><SelectValue placeholder="Size" /></SelectTrigger>
            <SelectContent>{TSHIRT_SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Food Preference</Label>
          <Select value={person.food_preference || 'none'} onValueChange={(v) => onChange({ ...person, food_preference: v === 'none' ? '' : v })}>
            <SelectTrigger data-testid={`${testPrefix}-food`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {FOOD_PREFS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Self Rating</Label>
          <Select value={person.self_rating || 'none'} onValueChange={(v) => onChange({ ...person, self_rating: v === 'none' ? '' : v })}>
            <SelectTrigger data-testid={`${testPrefix}-self-rating`}><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">—</SelectItem>
              {SELF_RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function RegisterDialog({ open, onClose, event, onDone }) {
  const [custom, setCustom] = useState({});
  const [player, setPlayer] = useState(emptyPerson());
  const [partners, setPartners] = useState({});  // { [category_id]: personObj }
  const [selected, setSelected] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(0);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [waiver, setWaiver] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setPlayer(emptyPerson()); setPartners({}); setSelected([]);
      setCouponCode(''); setDiscount(0); setPaymentMode('Cash'); setWaiver(true); setNotes(''); setCustom({});
    }
  }, [open]);

  const cats = event?.categories || [];
  const partnerCats = cats.filter((c) => selected.includes(c.id) && (c.format === 'doubles' || c.format === 'mixed'));
  const subtotal = cats.filter((c) => selected.includes(c.id)).reduce((s, c) => s + (Number(c.fee) || 0), 0);
  const total = Math.max(0, subtotal - (Number(discount) || 0));
  const maxCats = event?.max_categories_per_player || 99;

  const toggleCat = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxCats) { toast.error(`Max ${maxCats} categories per player`); return prev; }
      return [...prev, id];
    });
  };

  const setPartnerFor = (catId, updater) => {
    setPartners((prev) => {
      const current = prev[catId] || emptyPerson();
      return { ...prev, [catId]: typeof updater === 'function' ? updater(current) : updater };
    });
  };

  const autoLookup = async (mobile, catId) => {
    try {
      const res = await axios.get(`${API}/events/player-lookup`, { params: { mobile } });
      if (!res.data.found) return;
      const p = res.data.player || {};
      const merge = (prev) => {
        const next = { ...prev, mobile };
        // Only fill *empty* fields — never overwrite what the admin already typed.
        Object.entries(p).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '' && !next[k]) next[k] = v;
        });
        return next;
      };
      if (catId) setPartnerFor(catId, merge); else setPlayer(merge);
      toast.success(`Auto-filled from ${res.data.source} records`, { id: `lookup-${catId || 'player'}` });
    } catch (e) { /* silent — auto lookup shouldn't nag on typos */ }
  };

  const submit = async () => {
    if (!player.name.trim() || player.mobile.length !== 10) { toast.error('Enter player name and valid 10-digit mobile'); return; }
    if (missingCustom(event?.custom_fields, custom).length) { toast.error('Fill all required custom questions'); return; }
    if (!selected.length) { toast.error('Select at least one category'); return; }
    const partnersPayload = partnerCats
      .filter((c) => partners[c.id] && (partners[c.id].name?.trim() || partners[c.id].mobile?.trim()))
      .map((c) => ({
        category_id: c.id,
        partner: { ...partners[c.id], dupr_rating: partners[c.id].dupr_rating ? Number(partners[c.id].dupr_rating) : null },
      }));
    setSaving(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/register`, {
        player: { ...player, dupr_rating: player.dupr_rating ? Number(player.dupr_rating) : null },
        partners: partnersPayload,
        category_ids: selected,
        coupon_code: couponCode, discount: Number(discount) || 0,
        payment_mode: paymentMode, waiver_accepted: waiver, notes,
        custom_responses: custom,
      });
      if (res.data.waitlist) toast.warning(`Registered on WAITLIST for: ${res.data.waitlist_categories.join(', ')}`);
      else toast.success('Registration confirmed');
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.error === 'eligibility_failed') {
        toast.error(d.messages.join(' • '));
      } else {
        toast.error(typeof d === 'string' ? d : 'Registration failed');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto" data-testid="register-dialog">
        <DialogHeader><DialogTitle>New Registration — {event?.name}</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <Card className="p-4"><h4 className="font-semibold mb-3 text-slate-800">Player Details</h4>
            <PersonForm label="Player" person={player} onChange={setPlayer} onAutoLookup={(m) => autoLookup(m, null)} testPrefix="reg-player" />
          </Card>

          <div className="space-y-2">
            <Label>Categories (max {maxCats})</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cats.map((c) => {
                const full = c.slots_left !== null && c.slots_left <= 0;
                return (
                  <label key={c.id} className={`flex items-center justify-between gap-2 p-2.5 border rounded-lg cursor-pointer ${selected.includes(c.id) ? 'border-primary bg-primary/5' : ''}`} data-testid={`reg-cat-${c.id}`}>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleCat(c.id)} />
                      <div>
                        <div className="font-medium text-sm">{c.name} <span className="text-xs text-slate-500">({c.format})</span></div>
                        <div className="text-xs text-slate-500">₹{c.fee} {c.slots_left !== null ? `· ${c.slots_left} left${full ? ' (waitlist)' : ''}` : ''}</div>
                      </div>
                    </div>
                    {full && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold">FULL</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {partnerCats.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`reg-partner-card-${c.id}`}>
              <h4 className="font-semibold mb-1 text-slate-800">Partner Details — {c.name}</h4>
              <p className="text-xs text-slate-400 mb-3">Optional</p>
              <PersonForm label="Partner" person={partners[c.id] || emptyPerson()} onChange={(p) => setPartnerFor(c.id, p)} onAutoLookup={(m) => autoLookup(m, c.id)} testPrefix={`reg-partner-${c.id}`} />
            </Card>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="reg-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {event?.allow_coupons && (
              <div className="space-y-1.5"><Label>Coupon Code</Label><Input value={couponCode} onChange={(e) => setCouponCode(e.target.value)} data-testid="reg-coupon" /></div>
            )}
            <div className="space-y-1.5"><Label>Manual Discount (₹)</Label><Input type="number" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="reg-discount" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
            <div className="sm:col-span-2"><CustomResponsesInputs fields={event?.custom_fields} values={custom} onChange={setCustom} testPrefix="reg-cf" /></div>
          </div>

          <div className="p-3 rounded-lg bg-slate-50 border space-y-1 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between text-emerald-700"><span>Discount</span><span>− ₹{(Number(discount) || 0).toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between font-bold text-base border-t pt-1"><span>Total</span><span data-testid="reg-total">₹{total.toLocaleString('en-IN')}</span></div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={waiver} onChange={(e) => setWaiver(e.target.checked)} data-testid="reg-waiver" />
            Player has accepted the waiver / terms
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="reg-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="reg-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Register & Collect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const PAY_BADGE = { paid: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', failed: 'bg-red-100 text-red-700', refunded: 'bg-purple-100 text-purple-700' };
const REG_BADGE = { confirmed: 'bg-emerald-100 text-emerald-700', pending_payment: 'bg-orange-100 text-orange-700', waitlist: 'bg-amber-100 text-amber-700', cancelled: 'bg-red-100 text-red-700' };

function SendEventUpdateDialog({ open, onClose, event, registrations }) {
  const [scope, setScope] = React.useState('both');
  const [templateName, setTemplateName] = React.useState('event_participant_update');
  const [message, setMessage] = React.useState('');
  const [link, setLink] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setScope('both'); setMessage(''); setLink('');
      setTemplateName('event_participant_update'); setResult(null);
    }
  }, [open]);

  // Recipient count preview (deduped, valid mobiles, excludes cancelled).
  const recipientCount = React.useMemo(() => {
    const clean = (m) => {
      const d = (m || '').replace(/\D/g, '');
      if (d.length === 10) return `91${d}`;
      if (d.length === 12 && d.startsWith('91')) return d;
      return '';
    };
    const set = new Set();
    (registrations || []).forEach((r) => {
      if (r.status === 'cancelled') return;
      if (scope !== 'partner_only') {
        const m = clean(r.player?.mobile);
        if (m) set.add(m);
      }
      if (scope !== 'player_only') {
        const m = clean(r.partner?.mobile);
        if (m) set.add(m);
      }
    });
    return set.size;
  }, [registrations, scope]);

  // Preview using the first eligible recipient
  const previewName = React.useMemo(() => {
    for (const r of (registrations || [])) {
      if (r.status === 'cancelled') continue;
      if (scope !== 'partner_only' && r.player?.name) return r.player.name;
      if (scope !== 'player_only' && r.partner?.name) return r.partner.name;
    }
    return 'Player';
  }, [registrations, scope]);
  const preview = (message || '').trim()
    ? `Hi ${previewName},\n\nUpdate for ${event?.name || 'Event'}:\n${message}${link ? `\n\n${link}` : ''}`
    : '(Enter a message to see the preview)';

  const submit = async () => {
    if (!message.trim()) { toast.error('Enter a custom update message'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/send-update`, {
        recipients: scope, message: message.trim(), link: link.trim(),
        template_name: templateName.trim() || undefined,
      });
      setResult(res.data);
      toast.success(`Sent ${res.data.sent} / ${res.data.total_selected}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send update');
    } finally { setSending(false); }
  };

  const downloadReport = () => {
    if (!result?.log_id) return;
    const url = `${API}/events/${event.id}/send-update/${result.log_id}/export`;
    // Include the auth token via axios so admin-only route works; then trigger download.
    axios.get(url, { responseType: 'blob' }).then((res) => {
      const blob = new Blob([res.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `event_update_${result.log_id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    }).catch(() => toast.error('Download failed'));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[640px] max-h-[92vh] overflow-y-auto" data-testid="send-update-dialog">
        <DialogHeader>
          <DialogTitle>Send Event Update — {event?.name}</DialogTitle>
        </DialogHeader>

        {result ? (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3 text-center">
              <Card className="p-3"><div className="text-xs text-slate-500">Total</div><div className="text-2xl font-bold" data-testid="result-total">{result.total_selected}</div></Card>
              <Card className="p-3 bg-emerald-50 border-emerald-200"><div className="text-xs text-emerald-700">Sent</div><div className="text-2xl font-bold text-emerald-700" data-testid="result-sent">{result.sent}</div></Card>
              <Card className="p-3 bg-red-50 border-red-200"><div className="text-xs text-red-700">Failed</div><div className="text-2xl font-bold text-red-700" data-testid="result-failed">{result.failed}</div></Card>
              <Card className="p-3 bg-amber-50 border-amber-200"><div className="text-xs text-amber-700">Skipped</div><div className="text-2xl font-bold text-amber-700" data-testid="result-skipped">{result.skipped}</div></Card>
            </div>
            <div className="border rounded-md max-h-[300px] overflow-y-auto text-xs">
              <table className="w-full">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="text-left px-2 py-1.5">Recipient</th>
                  <th className="text-left px-2 py-1.5">Mobile</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                  <th className="text-left px-2 py-1.5">Note</th>
                </tr></thead>
                <tbody>{result.results.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1">{r.name} <span className="text-slate-400">({r.role})</span></td>
                    <td className="px-2 py-1 font-mono">{r.mobile}</td>
                    <td className="px-2 py-1">
                      <span className={`px-1.5 py-0.5 rounded ${r.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : r.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                    </td>
                    <td className="px-2 py-1 text-slate-500 truncate max-w-[220px]">{r.error || r.reason || r.message_id || ''}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={downloadReport} data-testid="download-report-btn"><Download className="w-4 h-4 mr-1" /> Download Report</Button>
              <Button onClick={onClose}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Template Name</Label>
                <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} data-testid="update-template-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Recipient</Label>
                <Select value={scope} onValueChange={setScope}>
                  <SelectTrigger data-testid="update-scope"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">Player + Partner (default)</SelectItem>
                    <SelectItem value="player_only">Player only</SelectItem>
                    <SelectItem value="partner_only">Partner only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="p-3 rounded-md bg-slate-50 border text-xs flex justify-between">
              <span className="text-slate-500">Total valid recipients (deduped)</span>
              <span className="font-bold" data-testid="recipient-count">{recipientCount}</span>
            </div>
            <div className="space-y-1.5">
              <Label>Custom Update Message</Label>
              <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Slot allocations are out — check the bracket link below." data-testid="update-message" />
            </div>
            <div className="space-y-1.5">
              <Label>Custom Link (optional)</Label>
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://..." data-testid="update-link" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Preview</Label>
              <div className="mt-1 p-3 border rounded-md bg-emerald-50/40 whitespace-pre-wrap text-sm font-mono text-slate-700" data-testid="update-preview">{preview}</div>
              <div className="text-[11px] text-slate-400 mt-1">Template <b>{templateName || 'event_participant_update'}</b> · {'{{'}1{'}}'}=name · {'{{'}2{'}}'}=event · {'{{'}3{'}}'}=message · {'{{'}4{'}}'}=link</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={sending || recipientCount === 0} className="bg-emerald-600 hover:bg-emerald-700" data-testid="update-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Send to {recipientCount}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function EditRegistrationDialog({ open, onClose, registration, event, onSaved }) {
  const [player, setPlayer] = useState(emptyPerson());
  const [partners, setPartners] = useState({});  // { [category_id]: personObj }
  const [selected, setSelected] = useState([]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [status, setStatus] = useState('confirmed');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState({});
  const [cfMissing, setCfMissing] = useState([]);

  useEffect(() => {
    if (!open || !registration) return;
    setCfMissing([]);
    setCustom(responsesToMap(registration.custom_responses));
    const rp = registration.player || {};
    // Normalize mobile: strip country code prefix (91XXXXXXXXXX → XXXXXXXXXX)
    const normMobile = (m) => {
      const d = (m || '').replace(/\D/g, '');
      if (d.length === 12 && d.startsWith('91')) return d.slice(2);
      return d.length > 10 ? d.slice(-10) : d;
    };
    setPlayer({ ...emptyPerson(), ...rp, mobile: normMobile(rp.mobile || ''), dupr_rating: rp.dupr_rating ?? '' });
    const existingPartners = registration.partners
      || (registration.partner ? [{ category_id: (registration.categories || [])[0]?.category_id, partner: registration.partner }] : []);
    const partnersMap = {};
    existingPartners.forEach((entry) => {
      if (entry.category_id) partnersMap[entry.category_id] = { ...emptyPerson(), ...entry.partner, mobile: normMobile(entry.partner?.mobile || ''), dupr_rating: entry.partner?.dupr_rating ?? '' };
    });
    setPartners(partnersMap);
    setSelected((registration.categories || []).map((c) => c.category_id));
    setPaymentMode(registration.payment_mode || 'Cash');
    setPaymentStatus(registration.payment_status || 'pending');
    setStatus(registration.status || 'confirmed');
    setNotes(registration.notes || '');
  }, [open, registration]);

  const cats = event?.categories || [];
  const partnerCats = cats.filter((c) => selected.includes(c.id) && (c.format === 'doubles' || c.format === 'mixed'));
  const subtotal = cats.filter((c) => selected.includes(c.id)).reduce((s, c) => s + (Number(c.fee) || 0), 0);
  const maxCats = event?.max_categories_per_player || 99;

  const toggleCat = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= maxCats) { toast.error(`Max ${maxCats} categories per player`); return prev; }
      return [...prev, id];
    });
  };

  const setPartnerFor = (catId, updater) => {
    setPartners((prev) => {
      const current = prev[catId] || emptyPerson();
      return { ...prev, [catId]: typeof updater === 'function' ? updater(current) : updater };
    });
  };

  const autoLookup = async (mobile, catId) => {
    try {
      const res = await axios.get(`${API}/events/player-lookup`, { params: { mobile } });
      if (!res.data.found) return;
      const p = res.data.player || {};
      const merge = (prev) => {
        const next = { ...prev, mobile };
        Object.entries(p).forEach(([k, v]) => {
          if (v !== null && v !== undefined && v !== '' && !next[k]) next[k] = v;
        });
        return next;
      };
      if (catId) setPartnerFor(catId, merge); else setPlayer(merge);
      toast.success(`Auto-filled from ${res.data.source} records`, { id: `elookup-${catId || 'player'}` });
    } catch (e) { /* silent */ }
  };

  const submit = async () => {
    if (!player.name.trim() || (player.mobile || '').length !== 10) { toast.error('Enter player name and valid 10-digit mobile'); return; }
    if (!selected.length) { toast.error('Select at least one category'); return; }
    setCfMissing(missingCustom(event?.custom_fields, custom));
    const partnersPayload = partnerCats
      .filter((c) => partners[c.id] && (partners[c.id].name?.trim() || partners[c.id].mobile?.trim()))
      .map((c) => ({
        category_id: c.id,
        partner: { ...partners[c.id], dupr_rating: partners[c.id].dupr_rating !== '' && partners[c.id].dupr_rating !== null ? Number(partners[c.id].dupr_rating) : null },
      }));
    setSaving(true);
    try {
      const body = {
        player: { ...player, dupr_rating: player.dupr_rating !== '' && player.dupr_rating !== null ? Number(player.dupr_rating) : null },
        partners: partnersPayload,
        category_ids: selected,
        payment_mode: paymentMode,
        payment_status: paymentStatus,
        status,
        notes,
        ...((event?.custom_fields || []).length ? { custom_responses: custom } : {}),
      };
      await axios.put(`${API}/events/registrations/${registration.id}`, body);
      toast.success('Registration updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[760px] max-h-[92vh] overflow-y-auto" data-testid="edit-registration-dialog">
        <DialogHeader>
          <DialogTitle>Edit Registration — {registration?.registration_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <Card className="p-4"><h4 className="font-semibold mb-3 text-slate-800">Player Details</h4>
            <PersonForm label="Player" person={player} onChange={setPlayer} onAutoLookup={(m) => autoLookup(m, null)} testPrefix="edit-player" />
          </Card>

          <div className="space-y-2">
            <Label>Categories (max {maxCats})</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {cats.map((c) => (
                <label key={c.id} className={`flex items-center justify-between gap-2 p-2.5 border rounded-lg cursor-pointer ${selected.includes(c.id) ? 'border-primary bg-primary/5' : ''}`} data-testid={`edit-cat-${c.id}`}>
                  <div className="flex items-center gap-2">
                    <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleCat(c.id)} />
                    <div>
                      <div className="font-medium text-sm">{c.name} <span className="text-xs text-slate-500">({c.format})</span></div>
                      <div className="text-xs text-slate-500">₹{c.fee}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {partnerCats.map((c) => (
            <Card key={c.id} className="p-4" data-testid={`edit-partner-card-${c.id}`}>
              <h4 className="font-semibold mb-1 text-slate-800">Partner Details — {c.name}</h4>
              <p className="text-xs text-slate-400 mb-3">Optional</p>
              <PersonForm label="Partner" person={partners[c.id] || emptyPerson()} onChange={(p) => setPartnerFor(c.id, p)} onAutoLookup={(m) => autoLookup(m, c.id)} testPrefix={`edit-partner-${c.id}`} />
            </Card>
          ))}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Registration Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="edit-reg-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="pending_payment">Pending Payment</SelectItem>
                  <SelectItem value="waitlist">Waitlist</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Mode</Label>
              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger data-testid="edit-payment-mode"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Payment Status</Label>
              <Select value={paymentStatus} onValueChange={setPaymentStatus}>
                <SelectTrigger data-testid="edit-payment-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 sm:col-span-3"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" /></div>
            <div className="sm:col-span-3"><CustomResponsesInputs fields={event?.custom_fields} values={custom} onChange={setCustom} missing={cfMissing} testPrefix="edit-cf" /></div>
          </div>

          <div className="p-3 rounded-lg bg-slate-50 border text-sm flex justify-between">
            <span>Subtotal for selected categories</span>
            <span className="font-bold">₹{subtotal.toLocaleString('en-IN')}</span>
          </div>
          <p className="text-xs text-slate-400">Existing discount (₹{(registration?.discount || 0).toLocaleString('en-IN')}) is preserved. New total is recalculated on save.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-primary hover:bg-primary-hover" data-testid="edit-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RegistrationsPanel({ event, onBack, onRefreshEvent }) {
  const { isEventAdmin } = useAuth();
  const [data, setData] = useState({ registrations: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [multiCatOnly, setMultiCatOnly] = useState(false);
  const [sort, setSort] = useState({ key: 'registration_number', dir: 'desc' });
  const [showRegister, setShowRegister] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [refund, setRefund] = useState(0);
  const [showSendUpdate, setShowSendUpdate] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);

  const fetchRegs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/events/${event.id}/registrations`, { params });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRegs(); /* eslint-disable-next-line */ }, [statusFilter]);

  const markPaid = async (reg, status) => {
    try {
      await axios.post(`${API}/events/registrations/${reg.id}/mark-paid`, { payment_status: status });
      toast.success(`Marked ${status}`);
      fetchRegs();
    } catch (e) { toast.error('Failed to update payment'); }
  };

  const doCancel = async () => {
    try {
      await axios.post(`${API}/events/registrations/${cancelling.id}/cancel`, { refund_amount: Number(refund) || 0 });
      toast.success('Registration cancelled');
      setCancelling(null); setRefund(0);
      fetchRegs(); onRefreshEvent && onRefreshEvent();
    } catch (e) { toast.error('Failed to cancel'); }
  };

  const doDelete = async () => {
    if (!deleting) return;
    try {
      await axios.delete(`${API}/events/registrations/${deleting.id}`);
      toast.success('Registration deleted');
      setDeleting(null);
      fetchRegs(); onRefreshEvent && onRefreshEvent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const copyNumbers = async () => {
    const to91 = (m) => {
      const d = (m || '').replace(/\D/g, '');
      if (!d) return '';
      if (d.length === 10) return `91${d}`;
      if (d.length === 12 && d.startsWith('91')) return d;
      return d;
    };
    const set = new Set();
    (visibleRegs || []).forEach((r) => {
      if (r.status === 'cancelled') return;
      const a = to91(r.player?.mobile);
      const b = to91(r.partner?.mobile);
      if (a) set.add(a);
      if (b) set.add(b);
    });
    const list = [...set];
    if (!list.length) { toast.error('No mobile numbers in the current view'); return; }
    const text = list.join(', ');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${list.length} mobile${list.length > 1 ? 's' : ''} (player + partner, deduped)`);
    } catch {
      toast.error('Clipboard blocked — select and copy manually');
    }
  };

  const [abandoned, setAbandoned] = useState([]);
  const [showAbandoned, setShowAbandoned] = useState(false);
  const [abandonedLoading, setAbandonedLoading] = useState(false);

  const loadAbandoned = async () => {
    setAbandonedLoading(true);
    try {
      const res = await axios.get(`${API}/events/${event.id}/abandoned`);
      setAbandoned(res.data);
    } catch (e) { toast.error('Failed to load abandoned registrations'); }
    finally { setAbandonedLoading(false); }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.id}/registrations/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `registrations_${event.name}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  const openAudit = async () => {
    try {
      const res = await axios.get(`${API}/events/audit-log`, { params: { tournament_id: event.id } });
      setAudit(res.data); setShowAudit(true);
    } catch (e) { toast.error('Failed to load audit log'); }
  };

  const s = data.stats || {};
  const stats = [
    { label: 'Total', value: s.total || 0, icon: Users, color: 'text-slate-700' },
    { label: 'Paid', value: s.paid || 0, icon: CheckCircle, color: 'text-emerald-600' },
    { label: 'Pending', value: s.pending || 0, icon: Clock, color: 'text-amber-600' },
    { label: 'Waitlist', value: s.waitlist || 0, icon: Users, color: 'text-indigo-600' },
    { label: 'Collected', value: `₹${(s.collected || 0).toLocaleString('en-IN')}`, icon: IndianRupee, color: 'text-primary' },
  ];

  // ---- registrations per category (across confirmed + waitlist, exclude cancelled) ----
  const categoryCounts = React.useMemo(() => {
    const map = {};
    (event.categories || []).forEach((c) => { map[c.id] = { name: c.name, count: 0, id: c.id }; });
    (data.registrations || []).forEach((r) => {
      if (r.status === 'cancelled') return;
      (r.categories || []).forEach((c) => {
        if (map[c.category_id]) map[c.category_id].count += 1;
        else map[c.category_id] = { name: c.category_name || c.category_id, count: 1, id: c.category_id };
      });
    });
    return Object.values(map);
  }, [data.registrations, event.categories]);

  // ---- mobile numbers that appear in more than one non-cancelled registration ----
  const mobilesInMultiCats = React.useMemo(() => {
    const counts = new Map();
    (data.registrations || []).forEach((r) => {
      if (r.status === 'cancelled') return;
      const catCount = (r.categories || []).length;
      const pm = r.player?.mobile;
      if (pm) counts.set(pm, (counts.get(pm) || 0) + catCount);
      const partnerM = r.partner?.mobile;
      if (partnerM) counts.set(partnerM, (counts.get(partnerM) || 0) + catCount);
    });
    return new Set([...counts.entries()].filter(([, c]) => c > 1).map(([m]) => m));
  }, [data.registrations]);

  // ---- filter + sort ----
  const visibleRegs = React.useMemo(() => {
    let rows = data.registrations || [];
    if (categoryFilter !== 'all') {
      rows = rows.filter((r) => (r.categories || []).some((c) => c.category_id === categoryFilter));
    }
    if (multiCatOnly) {
      rows = rows.filter((r) => mobilesInMultiCats.has(r.player?.mobile)
                              || (r.partner?.mobile && mobilesInMultiCats.has(r.partner.mobile)));
    }
    const dir = sort.dir === 'asc' ? 1 : -1;
    const getVal = (r) => {
      switch (sort.key) {
        case 'registration_number': return r.registration_number || '';
        case 'name': return (r.player?.name || '').toLowerCase();
        case 'mobile': return r.player?.mobile || '';
        case 'partner_mobile': return r.partner?.mobile || '';
        case 'categories': return (r.categories || []).length;
        case 'reg_date': return r.created_at || '';
        case 'channel': return (r.source || '').toLowerCase();
        case 'total': return r.total_amount || 0;
        case 'payment': return r.payment_status || '';
        case 'status': return r.status || '';
        default: return '';
      }
    };
    return [...rows].sort((a, b) => {
      const av = getVal(a); const bv = getVal(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [data.registrations, categoryFilter, multiCatOnly, sort, mobilesInMultiCats]);

  const toggleSort = (key) => setSort((prev) => ({
    key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
  }));

  const renderSortIcon = (colKey) => {
    if (sort.key !== colKey) return <ArrowUpDown className="w-3 h-3 inline opacity-40" />;
    return sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 inline" /> : <ArrowDown className="w-3 h-3 inline" />;
  };

  // "Online" = came through the payment gateway (source == 'player'/'online');
  // "Offline" = added from the admin panel or backend seed script.
  const channelOf = (r) => (['player', 'online'].includes((r.source || '').toLowerCase()) ? 'Online' : 'Offline');

  const fmtRegDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    } catch { return iso; }
  };
  const fmtRegTime = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ''; }
  };

  return (
    <div className="space-y-5" data-testid="registrations-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="reg-back"><ArrowLeft className="w-4 h-4 mr-1" /> Events</Button>
        <h2 className="text-xl font-bold text-slate-900">{event.name} — Registrations</h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {stats.map((st) => (
          <Card key={st.label} className="p-3">
            <div className="flex items-center gap-2 text-xs text-slate-500"><st.icon className="w-3.5 h-3.5" /> {st.label}</div>
            <div className={`text-xl font-bold ${st.color}`} data-testid={`stat-${st.label.toLowerCase()}`}>{st.value}</div>
          </Card>
        ))}
      </div>

      {/* Registrations per category */}
      {categoryCounts.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="category-cards">
          {categoryCounts.map((c) => {
            const active = categoryFilter === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCategoryFilter(active ? 'all' : c.id)}
                className={`text-left p-2.5 rounded-lg border transition-colors ${active ? 'border-primary bg-primary/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                data-testid={`cat-card-${c.id}`}
              >
                <div className="text-xs text-slate-500 flex items-center gap-1"><Layers className="w-3 h-3" /> {c.name}</div>
                <div className={`text-lg font-bold ${active ? 'text-primary' : 'text-slate-800'}`}>{c.count}</div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-10" placeholder="Search name / mobile / reg no" value={search}
            onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchRegs()} data-testid="reg-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="reg-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="pending_payment">Pending Payment</SelectItem>
            <SelectItem value="waitlist">Waitlist</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]" data-testid="reg-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {(event.categories || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={multiCatOnly ? 'default' : 'outline'}
          onClick={() => setMultiCatOnly((v) => !v)}
          data-testid="reg-multi-cat-toggle"
          title="Show only players registered in more than one category"
        >
          <Layers className="w-4 h-4 mr-1" /> Multi-category
        </Button>
        <Button variant="outline" onClick={openAudit} data-testid="reg-audit-btn"><ScrollText className="w-4 h-4 mr-1" /> Audit</Button>
        <Button variant="outline" onClick={copyNumbers} data-testid="reg-copy-numbers-btn" title="Copy player + partner numbers (91-format, deduped) of currently visible rows">
          <ClipboardCopy className="w-4 h-4 mr-1" /> Copy Numbers
        </Button>
        {!isEventAdmin && <Button variant="outline" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50" onClick={() => setShowSendUpdate(true)} data-testid="reg-send-update-btn">
          <Send className="w-4 h-4 mr-1" /> Send Event Update
        </Button>}
        <Button variant="outline" onClick={exportCsv} data-testid="reg-export-btn"><Download className="w-4 h-4 mr-1" /> Export XLSX</Button>
        <Button variant="outline" className="border-orange-400 text-orange-700 hover:bg-orange-50" onClick={() => { setShowAbandoned(true); loadAbandoned(); }} data-testid="reg-abandoned-btn">
          <Ghost className="w-4 h-4 mr-1" /> Abandoned ({abandoned.length || '…'})
        </Button>
        <Button className="bg-primary hover:bg-primary-hover" onClick={() => setShowRegister(true)} data-testid="new-registration-btn"><UserPlus className="w-4 h-4 mr-1" /> New Registration</Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs">
            <tr>
              {[
                { key: 'name', label: 'Player' },
                { key: 'mobile', label: 'Mobile' },
                { key: 'partner_mobile', label: 'Partner Mobile' },
                { key: 'categories', label: 'Categories' },
                { key: 'reg_date', label: 'Reg Date' },
                { key: 'reg_date', label: 'Reg Time' },
                { key: 'channel', label: 'Channel' },
                { key: 'total', label: 'Total' },
                { key: 'payment', label: 'Payment' },
                { key: 'status', label: 'Status' },
              ].map((h, i) => (
                <th key={`${h.key}-${i}`} className="text-left px-3 py-2 font-medium cursor-pointer select-none"
                    onClick={() => toggleSort(h.key)}
                    data-testid={`sort-${h.key}-${i}`}>
                  <span className="inline-flex items-center gap-1">{h.label} {renderSortIcon(h.key)}</span>
                </th>
              ))}
              <th className="text-left px-3 py-2 font-medium">Actions</th>
              <th className="text-left px-3 py-2 font-medium cursor-pointer select-none"
                  onClick={() => toggleSort('registration_number')}
                  data-testid="sort-registration_number">
                <span className="inline-flex items-center gap-1">Reg No {renderSortIcon('registration_number')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
            ) : visibleRegs.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-8 text-slate-400">No registrations match the current filters</td></tr>
            ) : visibleRegs.map((r) => {
              const channel = channelOf(r);
              const partnerEntries = r.partners || (r.partner ? [{ partner: r.partner }] : []);
              const partnerNames = partnerEntries.map((pe) => pe.partner?.name).filter(Boolean).join(' + ');
              const partnerMobiles = partnerEntries.map((pe) => pe.partner?.mobile).filter(Boolean).join(', ');
              return (
              <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`reg-row-${r.id}`}>
                <td className="px-3 py-2 font-medium">
                  <span>{r.player?.name}</span>
                  {partnerNames ? <span className="text-slate-400"> + {partnerNames}</span> : ''}
                  {(r.custom_responses || []).filter((cr) => cr.value).length > 0 && (
                    <div className="text-[11px] text-slate-500 font-normal mt-0.5" data-testid={`reg-custom-${r.id}`}>
                      {(r.custom_responses || []).filter((cr) => cr.value).map((cr) => <div key={cr.field_id} className="truncate max-w-[260px]" title={`${cr.label}: ${cr.value}`}><b className="text-slate-600">{cr.label}:</b> {cr.value}</div>)}
                    </div>
                  )}
                  {(mobilesInMultiCats.has(r.player?.mobile) || partnerEntries.some((pe) => pe.partner?.mobile && mobilesInMultiCats.has(pe.partner.mobile))) && (
                    <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700" title="Registered in multiple categories">MULTI</span>
                  )}
                </td>
                <td className="px-3 py-2">{r.player?.mobile || ''}</td>
                <td className="px-3 py-2 text-slate-600">{partnerMobiles || '—'}</td>
                <td className="px-3 py-2 text-xs">{(r.categories || []).map((c) => c.category_name).join(', ')}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{fmtRegDate(r.created_at)}</td>
                <td className="px-3 py-2 text-xs whitespace-nowrap font-mono">{fmtRegTime(r.created_at)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${channel === 'Online' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}`}
                        data-testid={`channel-${r.id}`}>{channel}</span>
                </td>
                <td className="px-3 py-2">₹{(r.total_amount || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${PAY_BADGE[r.payment_status] || 'bg-slate-100'}`}>{statusLabel(r.payment_status)}</span></td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${REG_BADGE[r.status] || 'bg-slate-100'}`}>{statusLabel(r.status)}</span></td>
                <td className="px-3 py-2">
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing(r)} data-testid={`edit-reg-${r.id}`}><Pencil className="w-3 h-3 mr-1" />Edit</Button>
                    {r.status !== 'cancelled' && r.payment_status !== 'paid' && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markPaid(r, 'paid')} data-testid={`mark-paid-${r.id}`}>Mark Paid</Button>
                    )}
                    {r.status !== 'cancelled' && (
                      <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => { setCancelling(r); setRefund(0); }} data-testid={`cancel-reg-${r.id}`}>Cancel</Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-700" onClick={() => setDeleting(r)} data-testid={`delete-reg-${r.id}`} title="Permanently delete registration"><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{r.registration_number}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <RegisterDialog open={showRegister} onClose={() => setShowRegister(false)} event={event}
        onDone={() => { setShowRegister(false); fetchRegs(); onRefreshEvent && onRefreshEvent(); }} />

      <EditRegistrationDialog
        open={!!editing}
        registration={editing}
        event={event}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); fetchRegs(); onRefreshEvent && onRefreshEvent(); }}
      />

      {/* Cancel dialog */}
      <Dialog open={!!cancelling} onOpenChange={() => setCancelling(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="cancel-dialog">
          <DialogHeader><DialogTitle>Cancel Registration</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Cancel <strong>{cancelling?.player?.name}</strong>&apos;s registration ({cancelling?.registration_number})?</p>
          <div className="space-y-1.5"><Label>Refund Amount (₹)</Label><Input type="number" value={refund} onChange={(e) => setRefund(e.target.value)} data-testid="refund-amount" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelling(null)}>Keep</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={doCancel} data-testid="confirm-cancel">Cancel Registration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="sm:max-w-[420px]" data-testid="delete-dialog">
          <DialogHeader><DialogTitle>Delete Registration</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Permanently delete <strong>{deleting?.player?.name}</strong>&apos;s registration
            <span className="ml-1 font-mono text-xs">({deleting?.registration_number})</span>?
            This cannot be undone — use <em>Cancel</em> instead if you need to keep a record.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Keep</Button>
            <Button className="bg-red-700 hover:bg-red-800 text-white" onClick={doDelete} data-testid="confirm-delete">Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEventUpdateDialog
        open={showSendUpdate}
        onClose={() => setShowSendUpdate(false)}
        event={event}
        registrations={data.registrations || []}
      />

      {/* Audit dialog */}
      <Dialog open={showAudit} onOpenChange={setShowAudit}>
        <DialogContent className="sm:max-w-[640px] max-h-[80vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader><DialogTitle>Audit Log — {event.name}</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {audit.length === 0 ? <p className="text-sm text-slate-400">No audit entries</p> : audit.map((a) => (
              <div key={a.id} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
                <div className="font-medium text-slate-800">{statusLabel(a.action)}</div>
                <div className="text-slate-600 text-xs">{a.details}</div>
                <div className="text-slate-400 text-[11px]">{a.actor} · {new Date(a.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Abandoned Registrations dialog */}
      <Dialog open={showAbandoned} onOpenChange={setShowAbandoned}>
        <DialogContent className="sm:max-w-[740px] max-h-[80vh] overflow-y-auto" data-testid="abandoned-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ghost className="w-5 h-5 text-orange-500" /> Abandoned / Incomplete Registrations
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-400">Visitors who opened the registration page but did not complete their registration. Excludes players who have an active confirmed registration.</p>
          {abandonedLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : abandoned.length === 0 ? (
            <div className="py-8 text-center text-slate-400 text-sm"><Ghost className="w-8 h-8 mx-auto mb-2 opacity-40" /> No abandoned visitors yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="abandoned-table">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="p-2 text-left">Stage</th>
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Mobile</th>
                    <th className="p-2 text-left">Last Activity</th>
                    <th className="p-2 text-left">First Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {abandoned.map((a, i) => (
                    <tr key={a.id || i} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="p-2">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${a.stage === 'started' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                          {a.stage === 'started' ? <CalendarX2 className="w-3 h-3" /> : <Ghost className="w-3 h-3" />}
                          {a.stage === 'started' ? 'Started' : 'Link Visit'}
                        </span>
                      </td>
                      <td className="p-2 font-medium text-slate-700">{a.customer_name || '—'}</td>
                      <td className="p-2 text-slate-600">{a.customer_mobile || '—'}</td>
                      <td className="p-2 text-slate-400 text-xs">{a.last_activity ? new Date(a.last_activity).toLocaleString() : '—'}</td>
                      <td className="p-2 text-slate-400 text-xs">{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
